import { ReactElement, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import classNames from 'classnames';
import Input from '~/ui/components/inputs/Input';
import AutoSubmit from '~/ui/components/inputs/AutoSubmit';
import { IStatusUsersFilters } from '~/services/api/users/types';
import SelectVirtualized from '~/ui/components/inputs/SelectVirtualized';
import { IIdName } from '~/services/api/types';
import api from '~/services/api';
import { extractErrorMessage } from '~/utils/error';
import { useStoreActions } from '~/store/hooks';
import styles from './Header.module.scss';
import { getInitialValues } from './form';
import Select from '~/ui/components/inputs/Select';
import { userRoleOptions } from '~/ui/constants/user';

const colors = {
  [true.toString()]: 'green',
  [false.toString()]: 'red',
};

interface IProps {
  isActive: boolean;
  total: number;
  onSubmit: (filters: IStatusUsersFilters) => void;
}

const Header = ({ isActive, total, onSubmit }: IProps): ReactElement => {
  const [programs, setPrograms] = useState<IIdName[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const showError = useStoreActions(actions => actions.snackbar.showError);

  const initialValues = getInitialValues();

  const formMethods = useForm<IStatusUsersFilters>({
    defaultValues: initialValues,
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const programOptions = useMemo(() => {
    const options = programs.map(item => ({
      value: item.id,
      label: item.name,
    }));

    return [
      {
        value: '',
        label: 'All Programs',
      },
      ...options,
    ];
  }, [programs.length]);

  const handleOnSubmit = (values: IStatusUsersFilters) => {
    onSubmit(values);
  };

  const onMount = async () => {
    setIsLoading(true);

    try {
      const selectors = await api.users.getSelectors().then(res => res.data);
      setPrograms(selectors.programs);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    onMount();
  }, []);

  return (
    <FormProvider {...formMethods}>
      <form className={styles.container} onSubmit={handleSubmit(handleOnSubmit)}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <Input name="fullName" label="Name" register={register} errors={errors} />
          </Grid>
          <Grid item xs>
            <Select
              name="roleId"
              control={control}
              errors={errors}
              options={userRoleOptions}
              label="Role"
            />
          </Grid>
          <Grid item xs>
            <SelectVirtualized
              name="programId"
              control={control}
              errors={errors}
              options={programOptions}
              isLoading={isLoading}
              label="Program Name"
            />
          </Grid>
          <Grid item>
            <Grid
              container
              className={classNames(styles.total, styles[colors[isActive.toString()]])}
              alignItems="center"
              justifyContent="center"
            >
              <Grid item>{total}</Grid>
            </Grid>
          </Grid>
          <AutoSubmit debounce={1000} initialValues={initialValues} onSubmit={handleOnSubmit} />
        </Grid>
      </form>
    </FormProvider>
  );
};

export default Header;
